import { HeadingElements } from "@elements/Typography/Heading";

export type JsonPrimitive = string | number | boolean | null;
export type JsonObject = { [key: string]: JsonValue | undefined };
export type JsonArray = (JsonValue | undefined)[];
export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

export type OpenSearchShards = {
  total: number;
  successful: number;
  skipped: number;
  failed: number;
};

export type EscenicDefaultMetadata = Array<{ key: string; value: string }>;

export type CaasSqsMessage = {
  id?: string;
  title?: string;
  authors?: string[];
  operator?: string;
  homeSectionUrl?: string;
  homeSection?: string;
  source?: string;
  sourceId?: string;
  type?: string;
  state?: string;
  publication?: string;
  url?: string;
};

export type OSResponse<T = unknown> = {
  _meta: Record<string, string>;
  payload: {
    shards?: OpenSearchShards;
    hits: {
      hits: {
        _index: string;
        _id: string;
        _score: null | number;
        _source: T;
        sort?: number[];
      }[];
      max_score: number | null;
      total: { value: number; relation: string };
    };
    took: number;
    timed_out: boolean;
  };
};

export type CueArticleSection = {
  name: string;
  uniqueName?: string;
  directoryName?: string;
  href?: string;
  parameters?: Array<{ key: string; value: string }>;
  parent?: SectionParent | null;
};

export type SectionParent = {
  name: string;
  uniqueName: string;
  directoryName?: string;
  parent?: SectionParent | null;
};

export type PartialCueArticleData<T = ProcessedArticleContext> = {
  context: T;
};

export type PartialCueArticle<T = ProcessedArticleContext> = {
  data: PartialCueArticleData<T>;
  sqsMessage: CaasSqsMessage;
};

export type ProcessedArticleContext = {
  created: string;
  updated: string;
  edited: string;
  authors?: {
    id?: number;
    name: string;
    profiles?: AuthorProfile[];
  }[];
  relatedContributorProfile?: {
    href: string;
    content?: contributorProfile;
  }[];
  elements: ProcessedArticleContextElement[];
  id: string;
  sections?: Array<CueArticleSection>;
  title: string;
  urlPath?: string;
  storyline: string[];
  media?: {
    content: {
      fields: { caption: string } & RawImageField;
    };
    href: string;
    summary: { key: string; value: string };
  }[];
  relatedStories: {
    content: RelatedStories;
  }[];
  tags?: {
    aliases?: string[];
    name: string;
    urlPath: string;
    fields?: Array<{ key: string; value: string }>;
  }[];
  // Mobile-specific optional fields (extended from web model for mobile BFF)
  typeName?: string;
  paidMode?: string;
  updatedTimestamp?: boolean;
  seoFields?: {
    metaTitle?: string;
    metaDescription?: string;
    metaRobots?: string;
    canonicalUrl?: string;
  };
  ogFields?: {
    ogTitle?: string;
    ogDescription?: string;
  };
  ogImages?: {
    content?: {
      fields?: {
        original?: RawImageField;
      };
    };
    href?: string;
  }[];
};

export type AuthorProfile = {
  content?: {
    urlPath: string;
    publication?: string;
    fields?: {
      name: string;
      email?: string;
      designation?: string;
    };
    headshotImage?: {
      content: {
        fields: {
          original: RawImageField;
          "square_480-caas": RawImageField;
          "square_320-caas": RawImageField;
          "square_560-caas": RawImageField;
          "original-caas": RawImageField;
        };
      };
    }[];
  };
};

export type ProcessedTagsContent = {
  name?: string;
  alias?: string;
};

export type CueUgcCampaignReferenceDetails = {
  id: string;
  type: string;
  fields: { title: string };
};

export type RawImageField = {
  url: string;
  width?: number;
  height?: number;
};

export type PartialCueArticleWithCampaign = PartialCueArticle & {
  data: {
    context: {
      campaignReference?: { content: CueUgcCampaignReferenceDetails }[];
    };
  };
};

export type RelatedStories = {
  id: string;
  urlPath?: string;
  fields: {
    title: string;
  };
};

export type contributorProfile = {
  urlPath: string;
  fields?: {
    name: string;
    email?: string;
  };
  headshotImage?: {
    content: {
      fields: {
        original: RawImageField;
        "square_480-caas": RawImageField;
        "square_320-caas": RawImageField;
        "square_560-caas": RawImageField;
        "original-caas": RawImageField;
      };
    };
  }[];
};

export type ProcessedAuthorContent = {
  name: string;
  email?: string;
  designation?: string;
  authorImage?: RawImageField;
  urlPath?: string;
};

export type ProcessedTextElement = {
  value: string;
  annotations: [] | ParagraphAnnotations[];
};

export type ProcessedArticleBody = (
  | ProcessedFullParagraph
  | ProcessedSubHead
  | ProcessedInlineImage
  | ProcessedSocialEmbed
  | ProcessedPullQuote
  | ProcessedListItem
  | ProcessedYoutubeVideo
  | ProcessedBrightcoveVideo
)[];

export type ProcessedSocialEmbed = ProcessedStorylineElement & {
  embedUrl: string;
  type: "embed";
};

export type ProcessedFullParagraph = ProcessedStorylineElement &
  ProcessedTextElement & {
    type: "paragraph";
  };

export type ProcessedStorylineElement = {
  id: string;
  position: number;
  type:
    | "paragraph"
    | "sub_head"
    | "image"
    | "embed"
    | "pull_quote"
    | "list_bulleted"
    | "list_numbered"
    | "youtube_video"
    | "brightcove_video";
};

export type ProcessedSubHead = ProcessedStorylineElement & {
  subhead: string;
  headingTag: HeadingElements;
  annotations: [] | ParagraphAnnotations[];
  type: "sub_head";
};

export type ProcessedInlineImage = ImageGallery &
  ProcessedStorylineElement & {
    type: "image";
  };

export enum MediaTypes {
  IMAGE = "image",
  BRIGHTCOVE_VIDEO = "brightcove_video",
  YOUTUBE_VIDEO = "youtube_video",
  UNKNOWN = "unknown",
}

export type MediaElementsBase = {
  id: string;
  type: MediaTypes;
};

export type BrightcoveVideoMedia = MediaElementsBase & {
  type: MediaTypes.BRIGHTCOVE_VIDEO;
  videoId: string;
};

export type YoutubeVideoMedia = MediaElementsBase & {
  type: MediaTypes.YOUTUBE_VIDEO;
  youtubeId: string;
};

export type ImageMediaElement = MediaElementsBase &
  ImageGallery & {
    type: MediaTypes.IMAGE;
  };

export type UnknownMediaElement = MediaElementsBase & {
  type: MediaTypes.UNKNOWN;
  id: string;
  name: string;
};

export type defaultImageElement = {
  type: string;
  caption: string;
  credit: string;
}[];

export type MediaCarouselItems = (
  | BrightcoveVideoMedia
  | YoutubeVideoMedia
  | ImageMediaElement
  | UnknownMediaElement
)[];

export type ImageGallery = {
  id: string;
  image: RawImageField | undefined;
  caption: string;
  credit: string;
  altText?: string;
};

export type ProcessedPullQuote = ProcessedStorylineElement & {
  quoteLabel?: ProcessedTextElement;
  quote: ProcessedTextElement;
  quotee: ProcessedTextElement;
  quoteCredit?: ProcessedTextElement;
  type: "pull_quote";
};

export type ProcessedListItem = ProcessedStorylineElement & {
  listStyle: string;
  listItem: ProcessedTextElement[];
  type: "list_bulleted" | "list_numbered";
};

export type ProcessedYoutubeVideo = ProcessedStorylineElement & {
  youtubeId: string;
  type: "youtube_video";
};

export type ProcessedBrightcoveVideo = ProcessedStorylineElement & {
  videoId: string;
  type: "brightcove_video";
};

export type ParagraphAnnotations = {
  index: number;
  length: number;
  name: string;
  value: string | ParagraphInlineLinkDetails;
};

export type ParagraphInlineLinkDetails = {
  url: string;
  target: "_self" | "_blank";
  rel: "nofollow" | "";
};

export type ContextElementField = {
  annotations: object[] | ParagraphAnnotations[];
  name: string;
  value: string;
  booleanValue?: boolean;
};

export type ProcessedArticleContextElement = {
  id: string;
  type: string | MediaTypes;
  fields: ContextElementField[];
  children: string[];
  relation?: {
    id: string;
    type: string;
    fields: RelationFields;
    urlPath: string;
  };
};

export type CropSelection = "portrait" | "landscape" | "square" | "wide";

export type RelationFields = {
  caption?: string;
  credit?: string;
  com_escenic_defaultmetadata?: EscenicDefaultMetadata;
  original?: RawImageField;
  portrait?: RawImageField;
  landscape?: RawImageField;
  landscape480?: RawImageField;
  landscape1024?: RawImageField;
  square?: RawImageField;
  square_30_26?: RawImageField;
  wide?: RawImageField;
  freecrop?: RawImageField;
  "original-caas"?: RawImageField;
  "portrait-caas"?: RawImageField;
  "landscape-caas"?: RawImageField;
  "square-caas"?: RawImageField;
  "square_30_26-caas"?: RawImageField;
  "wide-caas"?: RawImageField;
  "freecrop-caas"?: RawImageField;
};

export type StoryQueueItem = {
  content: {
    id: string;
    fields: { title: string };
  };
};

export type StoryQueue = {
  data: {
    context: {
      storyQueueList: StoryQueueItem[];
    };
  };
};
